import Job from 'jobs/Job'
import {
  ExtensionProgressionFlow,
  JobProgressionFlow,
  JobProgressionStep,
  JobProgressionStepStatus,
  OverrideableJobProgressionStepStatus,
} from 'jobs/progression/JobProgression'
import JobProgressionStore from 'jobs/progression/JobProgressionStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { match } from 'utils/pattern-match-util'
import { StartProgressionDialogViewModel } from './NoProgression/StartProgressionDialog/StartProgressionDialogViewModel'
import { DocumentCreationModalViewModel } from 'documents/views/DocumentCreationModal/DocumentCreationModalViewModel'
import Document from 'documents/Document'
import { WellKnownDocumentTypeShortId } from 'documents/dtos/document-type'
import { OpenItemsDialogViewModel } from './OpenItems/OpenItemsDialog/OpenItemsDialogViewModel'
import DocumentType from 'documents/DocumentType'
import { Nullable } from 'types/definitions'
import { Subscription } from 'rxjs'
import AvailableJobProgressionFlowsStore from 'jobs/progression/AvailableJobProgressionFlows/AvailableJobProgressionFlowsStore'
import { AlertFactory } from 'alerts/Alert'
import JobProgressionRequestDraftApprovalViewModel from './JobProgressionRequestDraftApprovalModal/JobProgressionRequestDraftApprovalViewModel'

enum JobProgressionDocumentCreationView {
  ReturnFiled,
  Extension,
}

const getExtensionStepDetails = (
  stepDetails: ExtensionProgressionStepsDetails,
  currentStep: JobProgressionStepStatus,
  progressionSteps: JobProgressionStep[] | undefined,
) => {
  if (currentStep === JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT) {
    const title = progressionSteps?.some(
      (p) => p.status === JobProgressionStepStatus.EXTENSION_ACCEPTED,
    )
      ? 'Extension accepted'
      : progressionSteps?.some(
            (p) => p.status === JobProgressionStepStatus.EXTENSION_REJECTED,
          )
        ? 'Extension rejected'
        : 'Waiting on client'

    return {
      ...stepDetails[JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT],
      title,
    }
  }

  return stepDetails[currentStep]
}

export default class JobProgressionViewModel {
  constructor(
    private progressionStore: JobProgressionStore,
    private availableJobProgressionFlowsStore: AvailableJobProgressionFlowsStore,
    private requestJobClose: () => void,
    private createDocumentCreationModalViewModel: () => DocumentCreationModalViewModel,
    private getJobDocuments: () => Document[],
    private promptForDocumentRequest: () => Promise<Nullable<DocumentType[]>>,
    public job: Job,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  @observable
  jobProgressionDocumentCreationVM: DocumentCreationModalViewModel | null = null

  @observable
  documentCreationToggledView: JobProgressionDocumentCreationView | null = null

  activate = task.resolved(async () => {
    await this.progressionStore.fetchProgression(this.job.id)
    await this.availableJobProgressionFlowsStore.fetchAvailableJobProgressionFlows(
      this.job.id,
    )

    this.subscription.add(
      this.progressionStore.events$.subscribe((jobId) => {
        if (jobId === this.job.id) {
          this.progressionStore.fetchProgression(this.job.id)
          this.availableJobProgressionFlowsStore.fetchAvailableJobProgressionFlows(
            this.job.id,
          )
        }
      }),
    )

    this.requestDraftReturnApprovalVM.activate()
  })

  deactivate() {
    this.activate.reset()
    this.subscription.unsubscribe()
  }

  /**
   * Dialog for requesting draft approval.
   */
  requestDraftReturnApprovalVM =
    new JobProgressionRequestDraftApprovalViewModel(
      this.createDocumentCreationModalViewModel,
    )

  /**
   * Dialog for setting job progression to job started.
   */
  startProgressionDialogVM = new StartProgressionDialogViewModel(
    this.progressionStore,
    this.job,
  )

  /**
   * Dialog for settings job progression to open items
   */
  openItemsDialogVM = new OpenItemsDialogViewModel(
    this.progressionStore,
    this.promptForDocumentRequest,
    this.job,
  )

  /**
   * Whether the document creation toggled view is ReturnFiled.
   */
  @computed
  get isDocumentCreationToggledViewReturnFiled() {
    return (
      this.documentCreationToggledView ===
      JobProgressionDocumentCreationView.ReturnFiled
    )
  }

  /**
   * Whether the document creation toggled view is Extension.
   */
  @computed
  get isDocumentCreationToggledViewExtension() {
    return (
      this.documentCreationToggledView ===
      JobProgressionDocumentCreationView.Extension
    )
  }

  /**
   * Progression steps for the current job.
   */
  @computed
  get progression() {
    return this.progressionStore?.progressionForJob(this.job.id)
  }

  /**
   * Available job progression flows for the current job.
   */
  @computed
  get availableJobProgressionFlows() {
    return this.availableJobProgressionFlowsStore.availableProgressionFlowsForJob(
      this.job.id,
    )
  }

  /**
   * Whether personal extension progression flow is available.
   */
  @computed
  get isPersonalExtensionAvailable() {
    return (
      this.availableJobProgressionFlows?.availableInventoryItemTaxExtensionFlows
        ?.personalExtensionProgressionFlowAvailable === true
    )
  }

  /**
   * Whether business extension progression flow is available.
   */
  @computed
  get isBusinessExtensionAvailable() {
    return (
      this.availableJobProgressionFlows?.availableInventoryItemTaxExtensionFlows
        ?.businessExtensionProgressionFlowAvailable === true
    )
  }

  /**
   * Whether outsourcing extension progression flow is available.
   */
  @computed
  get isOutsourcingExtensionAvailable() {
    return (
      this.availableJobProgressionFlows?.availableInventoryItemTaxExtensionFlows
        ?.outsourcingExtensionProgressionFlowAvailable === true
    )
  }

  /**
   * Whether any extension progression flow is available.
   */
  @computed
  get isAnyExtensionFlowAvailable() {
    return (
      this.isPersonalExtensionAvailable ||
      this.isBusinessExtensionAvailable ||
      this.isOutsourcingExtensionAvailable
    )
  }

  /**
   * Whether the current progression flow is an extension flow.
   */
  @computed
  get isExtensionFlow() {
    return (
      this.progression?.progressionFlow ===
        JobProgressionFlow.PERSONAL_EXTENSION ||
      this.progression?.progressionFlow ===
        JobProgressionFlow.BUSINESS_EXTENSION ||
      this.progression?.progressionFlow ===
        JobProgressionFlow.OUTSOURCING_EXTENSION
    )
  }

  @computed
  get progressionSteps() {
    return this.progression?.steps
  }

  @computed
  get jobProgressionStepStatus() {
    return this.progressionSteps?.find((s) => s.response === null)?.status
  }

  /**
   * The current progression step status.
   */
  @computed
  get currentStepStatus() {
    if (this.isExtensionFlow) {
      return (
        this.jobProgressionStepStatus ??
        JobProgressionStepStatus.EXTENSION_NOT_STARTED
      )
    }

    return this.jobProgressionStepStatus ?? JobProgressionStepStatus.NOT_STARTED
  }

  /**
   * Whether the job progression flow has been started or not.
   */
  @computed
  get hasStarted() {
    if (this.isExtensionFlow) {
      return (
        this.currentStepStatus !==
        JobProgressionStepStatus.EXTENSION_NOT_STARTED
      )
    }

    return this.currentStepStatus !== JobProgressionStepStatus.NOT_STARTED
  }

  /**
   * Whether the extension was rejected.
   */
  @computed
  get containsExtensionRejected() {
    return Boolean(
      this.progressionSteps?.some(
        (s) => s.status === JobProgressionStepStatus.EXTENSION_REJECTED,
      ),
    )
  }

  /**
   * Whether the extension was rejected.
   */
  @computed
  get isExtensionRejected() {
    return (
      this.currentStepStatus === JobProgressionStepStatus.EXTENSION_REJECTED
    )
  }

  @computed
  get submittedExtensionConfirmationRejection() {
    return this.getJobDocuments()
      .map((d) => d.documentType?.shortId)
      .some(
        (shortId) =>
          shortId ===
          WellKnownDocumentTypeShortId.ExtensionRejectionConfirmation,
      )
  }

  @computed
  get draftReturnDocument() {
    return this.getJobDocuments().find(
      (d) =>
        d.documentType?.shortId === WellKnownDocumentTypeShortId.DraftReturn &&
        !d.archived,
    )
  }

  /**
   * Whether a draft return exists in the documents for the job.
   */
  @computed
  get hasDraftReturn() {
    return Boolean(this.draftReturnDocument)
  }

  /**
   * Whether a draft return is downloadable for the job.
   */
  @computed
  get isDraftReturnDownloadable() {
    return Boolean(this.draftReturnDocument?.downloadable)
  }

  /**
   * Whether the extension was rejected.
   */
  @computed
  get isExtensionRejectedOrWaitingOnClient() {
    return (
      this.currentStepStatus === JobProgressionStepStatus.EXTENSION_REJECTED ||
      this.currentStepStatus ===
        JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT
    )
  }

  /**
   * Whether the current progression flow is Personal Extension.
   */
  @computed
  get isPersonalExtension() {
    return this.currentProgressionFlow === JobProgressionFlow.PERSONAL_EXTENSION
  }

  /**
   * Whether the current progression flow is Business Extension.
   */
  @computed
  get isBusinessExtension() {
    return this.currentProgressionFlow === JobProgressionFlow.BUSINESS_EXTENSION
  }

  /**
   * Whether the current progression flow is Outsourcing Extension.
   */
  @computed
  get isOutsourcingExtension() {
    return (
      this.currentProgressionFlow === JobProgressionFlow.OUTSOURCING_EXTENSION
    )
  }

  /**
   * Is current step status DRAFT_APPROVED
   */
  @computed
  get isCurrentStepStatusDraftApproved() {
    return this.currentStepStatus === JobProgressionStepStatus.DRAFT_APPROVED
  }

  /**
   * The current percentage of the progression flow completed.
   */
  @computed
  get completionStatus() {
    return this.job.completed
      ? 100
      : (this.currentStepInfo?.progress?.completed ?? 0)
  }

  /**
   * The current percentage of the progression flow pending completion.
   */
  @computed
  get pendingCompletionStatus() {
    return this.job.completed
      ? 0
      : (this.currentStepInfo?.progress?.pending ?? 0)
  }

  /**
   * The current progression flow.
   */
  @computed
  get currentProgressionFlow() {
    return this.progression?.progressionFlow ?? JobProgressionFlow.NONE
  }

  /**
   * Returns details for the current progression step.
   */
  @computed
  get currentStepInfo() {
    return match(this.currentProgressionFlow, {
      [JobProgressionFlow.OUTSOURCING]: () =>
        outsourcingProgressionStepsDetails[this.currentStepStatus],
      [JobProgressionFlow.RETAIL]: () =>
        retailProgressionStepsDetails[this.currentStepStatus],
      [JobProgressionFlow.ESSENTIAL]: () =>
        essentialProgressionStepsDetails[this.currentStepStatus],
      [JobProgressionFlow.PREPARATION]: () => {
        return preparationProgressionStepsDetails[this.currentStepStatus]
      },
      [JobProgressionFlow.REVIEW]: () => {
        return reviewProgressionStepsDetails[this.currentStepStatus]
      },
      [JobProgressionFlow.PERSONAL_EXTENSION]: () => {
        return getExtensionStepDetails(
          personalExtensionProgressionStepsDetails,
          this.currentStepStatus,
          this.progressionSteps,
        )
      },
      [JobProgressionFlow.BUSINESS_EXTENSION]: () => {
        return getExtensionStepDetails(
          businessExtensionProgressionStepsDetails,
          this.currentStepStatus,
          this.progressionSteps,
        )
      },
      [JobProgressionFlow.OUTSOURCING_EXTENSION]: () => {
        return outsourcingExtensionProgressionStepsDetails[
          this.currentStepStatus
        ]
      },
      [JobProgressionFlow.NONE]: () => undefined,
    })
  }

  @computed
  get isUploadDisabled() {
    if (!this.jobProgressionDocumentCreationVM) {
      return true
    }

    // If you can skip upload, enable upload button only if the Pro has uploaded documents.
    if (!this.isSkipUploadDisabled) {
      return !this.jobProgressionDocumentCreationVM.documentCreationItemVMs
        .length
    }

    return !this.jobProgressionDocumentCreationVM.documentCreationItemVMs
      .map((i) => i.selectedType?.shortId)
      .some((shortId) =>
        this.permittedDocumentTypesForFlow.includes(
          shortId as WellKnownDocumentTypeShortId,
        ),
      )
  }

  @computed
  get isSkipUploadDisabled() {
    // Review notes are optional for the review progression flow.
    if (this.currentProgressionFlow === JobProgressionFlow.REVIEW) {
      return false
    }

    const jobDocuments = this.getJobDocuments()

    return !jobDocuments
      .map((d) => d.documentType?.shortId)
      .some((shortId) =>
        this.permittedDocumentTypesForFlow.includes(
          shortId as WellKnownDocumentTypeShortId,
        ),
      )
  }

  get permittedDocumentTypesForFlow() {
    switch (this.currentProgressionFlow) {
      case JobProgressionFlow.RETAIL:
        return [
          WellKnownDocumentTypeShortId.FinalReturn,
          WellKnownDocumentTypeShortId.FinalReturnToBeMailed,
          WellKnownDocumentTypeShortId.ExtensionConfirmation,
        ]
      case JobProgressionFlow.ESSENTIAL:
        return [WellKnownDocumentTypeShortId.AnnotatedSourceDocument]
      case JobProgressionFlow.PREPARATION:
        return [WellKnownDocumentTypeShortId.PrepNotes]
      case JobProgressionFlow.REVIEW:
        return [WellKnownDocumentTypeShortId.ReviewNotes]
      case JobProgressionFlow.PERSONAL_EXTENSION:
      case JobProgressionFlow.BUSINESS_EXTENSION:
        if (this.isExtensionRejected) {
          return [WellKnownDocumentTypeShortId.ExtensionRejectionConfirmation]
        }

        return [WellKnownDocumentTypeShortId.ExtensionConfirmation]
      default:
        return []
    }
  }

  @computed
  get overrideableProgressionSteps(): [
    OverrideableJobProgressionStepStatus,
    ProgressionStepDetails,
  ][] {
    return match(this.currentProgressionFlow, {
      [JobProgressionFlow.RETAIL]: () => {
        if (
          this.currentStepStatus === JobProgressionStepStatus.DRAFT_IN_REVIEW
        ) {
          return [
            [
              OverrideableJobProgressionStepStatus.RETURN_FILED,
              retailProgressionStepsDetails[
                JobProgressionStepStatus.RETURN_FILED
              ]!,
            ],
            [
              OverrideableJobProgressionStepStatus.JOB_STARTED,
              retailProgressionStepsDetails[
                JobProgressionStepStatus.JOB_STARTED
              ]!,
            ],
            [
              OverrideableJobProgressionStepStatus.OPEN_ITEMS,
              retailProgressionStepsDetails[
                JobProgressionStepStatus.OPEN_ITEMS
              ]!,
            ],
          ]
        }
        return [
          [
            OverrideableJobProgressionStepStatus.RETURN_FILED,
            retailProgressionStepsDetails[
              JobProgressionStepStatus.RETURN_FILED
            ]!,
          ],
        ]
      },
      [JobProgressionFlow.OUTSOURCING]: () => {
        if (
          this.currentStepStatus === JobProgressionStepStatus.DRAFT_IN_REVIEW
        ) {
          return [
            [
              OverrideableJobProgressionStepStatus.JOB_STARTED,
              retailProgressionStepsDetails[
                JobProgressionStepStatus.JOB_STARTED
              ]!,
            ],
            [
              OverrideableJobProgressionStepStatus.OPEN_ITEMS,
              retailProgressionStepsDetails[
                JobProgressionStepStatus.OPEN_ITEMS
              ]!,
            ],
          ]
        }
        return []
      },
      [JobProgressionFlow.ESSENTIAL]: () => [],
      [JobProgressionFlow.NONE]: () => [],
      [JobProgressionFlow.PREPARATION]: () => [],
      [JobProgressionFlow.REVIEW]: () => {
        if (
          this.currentStepStatus === JobProgressionStepStatus.DRAFT_IN_REVIEW
        ) {
          return [
            [
              OverrideableJobProgressionStepStatus.JOB_STARTED,
              retailProgressionStepsDetails[
                JobProgressionStepStatus.JOB_STARTED
              ]!,
            ],
            [
              OverrideableJobProgressionStepStatus.OPEN_ITEMS,
              retailProgressionStepsDetails[
                JobProgressionStepStatus.OPEN_ITEMS
              ]!,
            ],
          ]
        }
        return []
      },
      [JobProgressionFlow.PERSONAL_EXTENSION]: () => [],
      [JobProgressionFlow.BUSINESS_EXTENSION]: () => [],
      [JobProgressionFlow.OUTSOURCING_EXTENSION]: () => [],
    })
  }

  /**
   * Sets the current progression to job started.
   */
  answerJobStarted = task.resolved(async () => {
    await this.progressionStore.setJobStartedProgression(this.job.id)
  })

  /**
   * Sets the current progression to personal extension in progress.
   */
  answerJobPersonalExtensionInProgress = task.resolved(async () => {
    await this.progressionStore.SetExtensionInProgressProgression(
      this.job.id,
      ExtensionProgressionFlow.PERSONAL,
    )
  })

  /**
   * Sets the current progression to business extension in progress.
   */
  answerJobBusinessExtensionInProgress = task.resolved(async () => {
    await this.progressionStore.SetExtensionInProgressProgression(
      this.job.id,
      ExtensionProgressionFlow.BUSINESS,
    )
  })

  /**
   * Sets the current progression to outsourcing extension in progress.
   */
  answerJobOutsourcingExtensionInProgress = task.resolved(async () => {
    await this.progressionStore.SetExtensionInProgressProgression(
      this.job.id,
      ExtensionProgressionFlow.OUTSOURCING,
    )
  })

  /**
   * Sets the current progression to extension accepted.
   */
  answerJobExtensionAccepted = task.resolved(async () => {
    await this.progressionStore.SetExtensionAcceptedProgression(this.job.id)
  })

  /**
   * Sets the current progression to extension rejected.
   */
  answerJobExtensionRejected = task.resolved(async () => {
    await this.progressionStore.SetExtensionRejectedProgression(this.job.id)
  })

  /**
   * Sets the current progression to ready to file.
   */
  answerReadyToFile = task.resolved(async (setAsAdmin: boolean) => {
    await this.progressionStore
      .SetReadyToFileProgression(this.job.id, setAsAdmin)
      .catch((err) => this.newAlert().fromError(err).show())
  })

  /**
   * Shows the documents received dialog.
   */
  showJobStartedDialog() {
    this.startProgressionDialogVM.show()
  }

  preparationCompleted = task.resolved(async () => {
    await this.progressionStore.setPreparationJobCompleted(this.job.id)
  })

  /**
   * Sets the current progression to authorization requested.
   */
  answerAuthorizationRequested = task.resolved(async () => {
    await this.progressionStore.setAuthorizationRequestedProgression(
      this.job.id,
    )
  })

  /**
   * Sets the current progression to authorization signed.
   */
  answerAuthorizationSigned = task.resolved(async () => {
    await this.progressionStore.setAuthorizationSignedProgression(this.job.id)
  })

  /**
   * Sets the current progression to return filed.
   */
  answerReturnFiled = task.resolved(async (skipDocumentUpload: boolean) => {
    if (!this.jobProgressionDocumentCreationVM) {
      return
    }

    if (!skipDocumentUpload) {
      const uploadSuccess =
        await this.jobProgressionDocumentCreationVM.uploadFiles()
      if (!uploadSuccess) {
        return
      }
    }

    switch (this.currentProgressionFlow) {
      case JobProgressionFlow.RETAIL:
        await this.progressionStore.setReturnFiledProgression(this.job.id)
        break
      case JobProgressionFlow.ESSENTIAL:
        await this.progressionStore.setDeliverableCompletedProgression(
          this.job.id,
        )
        break
      case JobProgressionFlow.PREPARATION:
        await this.progressionStore.setPreparationJobCompleted(this.job.id)
        break
      case JobProgressionFlow.REVIEW:
        await this.progressionStore.setReviewJobCompleted(this.job.id)
        break
    }
    this.closeDocumentCreationModal()
  })

  /**
   * Sets the current progression to extension confirmed.
   */
  answerExtensionConfirmed = task.resolved(
    async (skipDocumentUpload: boolean) => {
      if (!this.jobProgressionDocumentCreationVM) {
        return
      }

      if (!skipDocumentUpload) {
        const uploadSuccess =
          await this.jobProgressionDocumentCreationVM.uploadFiles()
        if (!uploadSuccess) {
          return
        }
      }

      switch (this.currentProgressionFlow) {
        case JobProgressionFlow.PERSONAL_EXTENSION:
        case JobProgressionFlow.BUSINESS_EXTENSION:
          await this.progressionStore.SetExtensionConfirmedProgression(
            this.job.id,
          )
          break
      }
      this.closeDocumentCreationModal()
    },
  )

  /**
   * Uploads the extension rejected document.
   */
  uploadExtensionRejectedDocument = task.resolved(
    async (skipDocumentUpload: boolean) => {
      if (!this.jobProgressionDocumentCreationVM) {
        return
      }

      if (!skipDocumentUpload) {
        await this.jobProgressionDocumentCreationVM.uploadFiles()
      }

      this.closeDocumentCreationModal()
    },
  )

  /**
   * Overrides the current progression to specific step.
   */
  overrideProgressionStep = task.resolved(
    async (stepId: OverrideableJobProgressionStepStatus) => {
      await this.progressionStore.overrideProgressionStep(this.job.id, stepId)
    },
  )

  /**
   * Displays a modal to close out the current job.
   */
  @action.bound
  closeJob() {
    this.requestJobClose()
  }

  @action.bound
  viewReturnFiledDocumentCreationModal() {
    this.documentCreationToggledView =
      JobProgressionDocumentCreationView.ReturnFiled
    this.jobProgressionDocumentCreationVM =
      this.createDocumentCreationModalViewModel()
    this.jobProgressionDocumentCreationVM.show()
  }

  @action.bound
  viewExtensionDocumentCreationModal() {
    this.documentCreationToggledView =
      JobProgressionDocumentCreationView.Extension
    this.jobProgressionDocumentCreationVM =
      this.createDocumentCreationModalViewModel()
    this.jobProgressionDocumentCreationVM.show()
  }

  closeDocumentCreationModal() {
    this.jobProgressionDocumentCreationVM?.close()
    this.documentCreationToggledView = null
  }

  /**
   * Requests draft approval.
   */
  answerDraftInReview = task.resolved(async () => {
    if (
      this.progression?.progressionFlow !== JobProgressionFlow.OUTSOURCING &&
      this.progression?.progressionFlow !== JobProgressionFlow.REVIEW &&
      !this.hasDraftReturn
    ) {
      this.requestDraftReturnApprovalVM.show()
      return
    }

    await this.progressionStore.setDraftInReviewProgression(this.job.id)
  })
}

/**
 * Tracks the job progression completion at a step
 */
export interface ProgressionCompletion {
  completed: number
  pending: number
}

/**
 * Progression step details for populating the progression card UI.
 */
export interface StaticProgressionStepDetails {
  title: string
  description: string
  progress: ProgressionCompletion
}

interface ProgressionStepDetails {
  title: string
  proDescription: string
  clientDescription: string
  progress: ProgressionCompletion
}

type OutsourcingProgressionStepsDetails = Record<
  JobProgressionStepStatus,
  ProgressionStepDetails | undefined
>

const reviewProgressionStepsDetails: OutsourcingProgressionStepsDetails = {
  [JobProgressionStepStatus.NOT_STARTED]: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.JOB_STARTED]: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items.\n\u200B\nTake a moment to look over the prep notes left by the tax preparer.\n\u200B\nOnce you've completed the deliverable, request draft approval from your client.",
    clientDescription:
      'Your return is being reviewed. When ready, your Pro will contact you to review the draft return.',
    progress: { completed: 20, pending: 20 },
  },

  [JobProgressionStepStatus.OPEN_ITEMS]: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue reviewing.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 20, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_IN_REVIEW]: {
    title: 'Draft review',
    proDescription: 'Please wait for your client to review your deliverable.',
    clientDescription:
      'Your Pro has finalized a draft return for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 40, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_REJECTED]: {
    title: 'Changes requested',
    proDescription:
      "Once you're done making the required changes, request draft approval from your client. If anything is missing to complete job, please report open items.",
    clientDescription:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 20, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_APPROVED]: {
    title: 'Draft Approved',
    proDescription:
      'The draft has been approved. Please upload any review notes for the preparation pro.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 60, pending: 20 },
  },
  [JobProgressionStepStatus.REVIEW_COMPLETED]: {
    title: 'Review Completed',
    proDescription:
      'After uploading your review notes, please complete the job.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 80, pending: 20 },
  },
  [JobProgressionStepStatus.PREPARATION_COMPLETED]: undefined,
  [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: undefined,
  [JobProgressionStepStatus.RETURN_FILED]: undefined,
  [JobProgressionStepStatus.DELIVERABLE_COMPLETED]: undefined,
  [JobProgressionStepStatus.EXTENSION_NOT_STARTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_IN_PROGRESS]: undefined,
  [JobProgressionStepStatus.EXTENSION_CONFIRMED]: undefined,
  [JobProgressionStepStatus.EXTENSION_ACCEPTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_REJECTED]: undefined,
  [JobProgressionStepStatus.READY_TO_FILE]: undefined,
  [JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT]: undefined,
}

const preparationProgressionStepsDetails: OutsourcingProgressionStepsDetails = {
  [JobProgressionStepStatus.NOT_STARTED]: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 33 },
  },
  [JobProgressionStepStatus.JOB_STARTED]: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items.\n\u200B\nOnce you've finished the preparation, please upload the notes to the job.",
    clientDescription:
      'Your return is being prepared. Once your Pro has finished, they will upload their preparation notes.',
    progress: { completed: 40, pending: 40 },
  },

  [JobProgressionStepStatus.OPEN_ITEMS]: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue the preparation.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 40 },
  },
  [JobProgressionStepStatus.PREPARATION_COMPLETED]: {
    title: 'Preparation complete',
    proDescription:
      'Congratulations! All tasks are now complete.\n\u200B\nOnce the review is finished, the reviewer’s notes will be uploaded to the job.',
    clientDescription:
      'Your Pro has completed the preparation for your tax return. The preparation notes can be found in the Documents section.',
    progress: { completed: 80, pending: 20 },
  },

  [JobProgressionStepStatus.DRAFT_IN_REVIEW]: undefined,
  [JobProgressionStepStatus.DRAFT_REJECTED]: undefined,
  [JobProgressionStepStatus.DRAFT_APPROVED]: undefined,
  [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: undefined,
  [JobProgressionStepStatus.RETURN_FILED]: undefined,
  [JobProgressionStepStatus.DELIVERABLE_COMPLETED]: undefined,
  [JobProgressionStepStatus.REVIEW_COMPLETED]: undefined,
  [JobProgressionStepStatus.EXTENSION_NOT_STARTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_IN_PROGRESS]: undefined,
  [JobProgressionStepStatus.EXTENSION_CONFIRMED]: undefined,
  [JobProgressionStepStatus.EXTENSION_ACCEPTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_REJECTED]: undefined,
  [JobProgressionStepStatus.READY_TO_FILE]: undefined,
  [JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT]: undefined,
}

const outsourcingProgressionStepsDetails: OutsourcingProgressionStepsDetails = {
  [JobProgressionStepStatus.NOT_STARTED]: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.OPEN_ITEMS]: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue preparation.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.JOB_STARTED]: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items.\n\u200B\nOnce you've completed the deliverable, request draft approval from your client.",
    clientDescription:
      'A draft return is being prepared. When ready, your Pro will contact you to review.',
    progress: { completed: 33, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_IN_REVIEW]: {
    title: 'Draft review',
    proDescription: 'Please wait for your client to review your deliverable.',
    clientDescription:
      'Your Pro has finalized a draft return for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 66, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_REJECTED]: {
    title: 'Changes requested',
    proDescription:
      "Once you're done making the required changes, request draft approval from your client. If anything is missing to complete job, please report open items.",
    clientDescription:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 50, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_APPROVED]: {
    title: 'Draft approved',
    proDescription:
      'The client approved the deliverable. \n\u200B\n Check back in with the client to finalize the return and complete the job.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 80, pending: 20 },
  },
  [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: undefined,
  [JobProgressionStepStatus.RETURN_FILED]: undefined,
  [JobProgressionStepStatus.DELIVERABLE_COMPLETED]: undefined,
  [JobProgressionStepStatus.PREPARATION_COMPLETED]: undefined,
  [JobProgressionStepStatus.REVIEW_COMPLETED]: undefined,
  [JobProgressionStepStatus.EXTENSION_NOT_STARTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_IN_PROGRESS]: undefined,
  [JobProgressionStepStatus.EXTENSION_CONFIRMED]: undefined,
  [JobProgressionStepStatus.EXTENSION_ACCEPTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_REJECTED]: undefined,
  [JobProgressionStepStatus.READY_TO_FILE]: undefined,
  [JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT]: undefined,
}

type RetailProgressionStepsDetails = Record<
  JobProgressionStepStatus,
  ProgressionStepDetails | undefined
>

const retailProgressionStepsDetails: RetailProgressionStepsDetails = {
  [JobProgressionStepStatus.NOT_STARTED]: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.OPEN_ITEMS]: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue preparation.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.JOB_STARTED]: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items. \n\u200B\nOnce you've completed the deliverable, request draft approval from your client.",
    clientDescription:
      'A draft return is being prepared. When ready, your Pro will contact you to review.',
    progress: { completed: 20, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_IN_REVIEW]: {
    title: 'Draft review',
    proDescription: 'Please wait for your client to review your deliverable.',
    clientDescription:
      'Your Pro has a draft ready for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 40, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_REJECTED]: {
    title: 'Changes requested',
    proDescription:
      "Once you're done making the required changes, request draft approval from your client. If anything is missing to complete job, please report open items.",
    clientDescription:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 40, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_APPROVED]: {
    title: 'Draft approved',
    proDescription:
      'Once you have signed and uploaded **Form 8879**, proceed to the next step.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 50, pending: 20 },
  },
  [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: {
    title: 'Authorization requested',
    proDescription:
      'Contact the client and share **Form 8879**. Once e-file authorization is complete, move onto the next step.',
    clientDescription: '',
    progress: { completed: 60, pending: 20 },
  },
  [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: {
    title: 'Authorization received',
    proDescription:
      'File the return, and upload both the final return and e-file confirmation to proceed.',
    clientDescription: '',
    progress: { completed: 70, pending: 20 },
  },
  [JobProgressionStepStatus.RETURN_FILED]: {
    title: 'Return filed',
    proDescription:
      'You have filed the return. Check back in with the client to finalize and complete the job.',
    clientDescription: '',
    progress: { completed: 80, pending: 20 },
  },
  [JobProgressionStepStatus.DELIVERABLE_COMPLETED]: undefined,
  [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
  [JobProgressionStepStatus.PREPARATION_COMPLETED]: undefined,
  [JobProgressionStepStatus.REVIEW_COMPLETED]: undefined,
  [JobProgressionStepStatus.EXTENSION_NOT_STARTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_IN_PROGRESS]: undefined,
  [JobProgressionStepStatus.EXTENSION_CONFIRMED]: undefined,
  [JobProgressionStepStatus.EXTENSION_ACCEPTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_REJECTED]: undefined,
  [JobProgressionStepStatus.READY_TO_FILE]: undefined,
  [JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT]: undefined,
}

type EssentialProgressionStepsDetails = Record<
  JobProgressionStepStatus,
  ProgressionStepDetails | undefined
>

const essentialProgressionStepsDetails: EssentialProgressionStepsDetails = {
  [JobProgressionStepStatus.NOT_STARTED]: {
    title: 'Not started',
    proDescription: 'Confirm when you have started working on this job.',
    clientDescription:
      'A Pro will begin working on the job shortly. At this stage, we can no longer accept any documents.',
    progress: { completed: 0, pending: 33 },
  },
  [JobProgressionStepStatus.JOB_STARTED]: {
    title: 'Job in progress',
    proDescription:
      "Once you've completed the deliverable, upload the annotated source documents. If applicable, upload a missing items list.",
    clientDescription: 'The deliverable is being prepared.',
    progress: { completed: 33, pending: 33 },
  },
  [JobProgressionStepStatus.DELIVERABLE_COMPLETED]: {
    title: 'Job completed',
    proDescription:
      "You've successfully uploaded the deliverable. Complete the job to proceed.",
    clientDescription:
      'Your Pro has completed the work and will close the job soon.',
    progress: { completed: 67, pending: 33 },
  },
  [JobProgressionStepStatus.DRAFT_IN_REVIEW]: undefined,
  [JobProgressionStepStatus.DRAFT_REJECTED]: undefined,
  [JobProgressionStepStatus.DRAFT_APPROVED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: undefined,
  [JobProgressionStepStatus.OPEN_ITEMS]: undefined,
  [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
  [JobProgressionStepStatus.RETURN_FILED]: undefined,
  [JobProgressionStepStatus.PREPARATION_COMPLETED]: undefined,
  [JobProgressionStepStatus.REVIEW_COMPLETED]: undefined,
  [JobProgressionStepStatus.EXTENSION_NOT_STARTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_IN_PROGRESS]: undefined,
  [JobProgressionStepStatus.EXTENSION_CONFIRMED]: undefined,
  [JobProgressionStepStatus.EXTENSION_ACCEPTED]: undefined,
  [JobProgressionStepStatus.EXTENSION_REJECTED]: undefined,
  [JobProgressionStepStatus.READY_TO_FILE]: undefined,
  [JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT]: undefined,
}

type ExtensionProgressionStepsDetails = Record<
  JobProgressionStepStatus,
  ProgressionStepDetails | undefined
>

const personalExtensionProgressionStepsDetails: ExtensionProgressionStepsDetails =
  {
    [JobProgressionStepStatus.EXTENSION_NOT_STARTED]: {
      title: 'Not started',
      proDescription: 'Please confirm when you have begun working on this job.',
      clientDescription:
        'Please ensure that all necessary documents are prepared. Your pro will contact you if any additional information is needed.',
      progress: { completed: 0, pending: 20 },
    },
    [JobProgressionStepStatus.EXTENSION_IN_PROGRESS]: {
      title: 'Extension in progress',
      proDescription:
        'After filing the extension, please upload the e-file confirmation.',
      clientDescription:
        'Your extension is being prepared. Once your Pro has finished, they will upload the e-file confirmation.',
      progress: { completed: 20, pending: 20 },
    },
    [JobProgressionStepStatus.EXTENSION_CONFIRMED]: {
      title: 'Extension status',
      proDescription:
        'Notify the client once the IRS responds to the extension request. Let them know whether the extension has been accepted or rejected.',
      clientDescription:
        'Your pro has filed an extension on your behalf. You will receive an update when the IRS has either accepted or rejected the extension.',
      progress: { completed: 40, pending: 20 },
    },
    [JobProgressionStepStatus.EXTENSION_ACCEPTED]: {
      title: 'Extension accepted',
      proDescription:
        'The extension has been accepted by the IRS. You may complete the job.',
      clientDescription: 'The extension was successfully accepted by the IRS.',
      progress: { completed: 80, pending: 20 },
    },
    [JobProgressionStepStatus.EXTENSION_REJECTED]: {
      title: 'Extension rejected',
      proDescription:
        'The extension request was rejected. Please upload the rejection notice from the IRS. After uploading, you may complete the job.',
      clientDescription:
        'The extension was rejected by the IRS. This could be due to an already filed extension on your behalf. Your pro can answer any questions you might have.',
      progress: { completed: 80, pending: 20 },
    },
    [JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT]: {
      title: 'Waiting on client',
      proDescription:
        'Please wait on the client to notify you when they’re ready to continue filing.',
      clientDescription:
        'Please notify your pro when you’re ready to resume your tax return.',
      progress: { completed: 80, pending: 20 },
    },
    [JobProgressionStepStatus.NOT_STARTED]: undefined,
    [JobProgressionStepStatus.OPEN_ITEMS]: undefined,
    [JobProgressionStepStatus.JOB_STARTED]: undefined,
    [JobProgressionStepStatus.DRAFT_IN_REVIEW]: undefined,
    [JobProgressionStepStatus.DRAFT_REJECTED]: undefined,
    [JobProgressionStepStatus.DRAFT_APPROVED]: undefined,
    [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: undefined,
    [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: undefined,
    [JobProgressionStepStatus.RETURN_FILED]: undefined,
    [JobProgressionStepStatus.DELIVERABLE_COMPLETED]: undefined,
    [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
    [JobProgressionStepStatus.PREPARATION_COMPLETED]: undefined,
    [JobProgressionStepStatus.REVIEW_COMPLETED]: undefined,
    [JobProgressionStepStatus.READY_TO_FILE]: undefined,
  }

const businessExtensionProgressionStepsDetails: ExtensionProgressionStepsDetails =
  {
    [JobProgressionStepStatus.EXTENSION_NOT_STARTED]: {
      title: 'Not started',
      proDescription: 'Please confirm when you have begun working on this job.',
      clientDescription:
        'Please ensure that all necessary documents are prepared. Your pro will contact you if any additional information is needed.',
      progress: { completed: 0, pending: 20 },
    },
    [JobProgressionStepStatus.EXTENSION_IN_PROGRESS]: {
      title: 'Extension in progress',
      proDescription:
        'After filing the extension, please upload the e-file confirmation.',
      clientDescription:
        'Your extension is being prepared. Once your Pro has finished, they will upload the e-file confirmation.',
      progress: { completed: 20, pending: 20 },
    },
    [JobProgressionStepStatus.EXTENSION_CONFIRMED]: {
      title: 'Extension status',
      proDescription:
        'Notify the client once the IRS responds to the extension request. Let them know whether the extension has been accepted or rejected.',
      clientDescription:
        'Your pro has filed an extension on your behalf. You will receive an update when the IRS has either accepted or rejected the extension.',
      progress: { completed: 40, pending: 20 },
    },
    [JobProgressionStepStatus.EXTENSION_ACCEPTED]: {
      title: 'Extension accepted',
      proDescription:
        'The extension has been accepted by the IRS. You may complete the job.',
      clientDescription: 'The extension was successfully accepted by the IRS.',
      progress: { completed: 80, pending: 20 },
    },
    [JobProgressionStepStatus.EXTENSION_REJECTED]: {
      title: 'Extension rejected',
      proDescription:
        'The extension request was rejected. Please upload the rejection notice from the IRS. After uploading, you may complete the job.',
      clientDescription:
        'The extension was rejected by the IRS. This could be due to an already filed extension on your behalf. Your pro can answer any questions you might have.',
      progress: { completed: 80, pending: 20 },
    },
    [JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT]: {
      title: 'Waiting on client',
      proDescription:
        'Please wait on the client to notify you when they’re ready to continue filing.',
      clientDescription:
        'Please notify your pro when you’re ready to resume your tax return.',
      progress: { completed: 80, pending: 20 },
    },
    [JobProgressionStepStatus.NOT_STARTED]: undefined,
    [JobProgressionStepStatus.OPEN_ITEMS]: undefined,
    [JobProgressionStepStatus.JOB_STARTED]: undefined,
    [JobProgressionStepStatus.DRAFT_IN_REVIEW]: undefined,
    [JobProgressionStepStatus.DRAFT_REJECTED]: undefined,
    [JobProgressionStepStatus.DRAFT_APPROVED]: undefined,
    [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: undefined,
    [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: undefined,
    [JobProgressionStepStatus.RETURN_FILED]: undefined,
    [JobProgressionStepStatus.DELIVERABLE_COMPLETED]: undefined,
    [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
    [JobProgressionStepStatus.PREPARATION_COMPLETED]: undefined,
    [JobProgressionStepStatus.REVIEW_COMPLETED]: undefined,
    [JobProgressionStepStatus.READY_TO_FILE]: undefined,
  }

const outsourcingExtensionProgressionStepsDetails: ExtensionProgressionStepsDetails =
  {
    [JobProgressionStepStatus.EXTENSION_NOT_STARTED]: {
      title: 'Not started',
      proDescription: 'Please confirm when you have begun working on this job.',
      clientDescription:
        'Please ensure that all necessary documents are prepared. Your pro will contact you if any additional information is needed.',
      progress: { completed: 0, pending: 20 },
    },
    [JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT]: {
      title: 'Waiting on client',
      proDescription:
        'Please wait on the client to notify you when they’re ready to continue.',
      clientDescription:
        'Please notify your pro when you’re ready to continue.',
      progress: { completed: 80, pending: 20 },
    },
    [JobProgressionStepStatus.READY_TO_FILE]: {
      title: 'Extension completed',
      proDescription:
        'The extension has been completed. You may complete the job.',
      clientDescription:
        'The extension was successfully completed by your Pro.',
      progress: { completed: 100, pending: 0 },
    },
    [JobProgressionStepStatus.NOT_STARTED]: undefined,
    [JobProgressionStepStatus.OPEN_ITEMS]: undefined,
    [JobProgressionStepStatus.JOB_STARTED]: undefined,
    [JobProgressionStepStatus.DRAFT_IN_REVIEW]: undefined,
    [JobProgressionStepStatus.DRAFT_REJECTED]: undefined,
    [JobProgressionStepStatus.DRAFT_APPROVED]: undefined,
    [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: undefined,
    [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: undefined,
    [JobProgressionStepStatus.RETURN_FILED]: undefined,
    [JobProgressionStepStatus.DELIVERABLE_COMPLETED]: undefined,
    [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
    [JobProgressionStepStatus.PREPARATION_COMPLETED]: undefined,
    [JobProgressionStepStatus.REVIEW_COMPLETED]: undefined,
    [JobProgressionStepStatus.EXTENSION_IN_PROGRESS]: undefined,
    [JobProgressionStepStatus.EXTENSION_CONFIRMED]: undefined,
    [JobProgressionStepStatus.EXTENSION_ACCEPTED]: undefined,
    [JobProgressionStepStatus.EXTENSION_REJECTED]: undefined,
  }
